import { z } from 'zod';
import { baseComponentSchema, imageCropSchema, buttonSchema } from '../component-base.schemas';

// Image Gallery component schemas
export const imageGalleryItemSchema = z.object({
  id: z.string().uuid('Invalid gallery item ID'),
  imageUrl: z.string().url('Invalid image URL').optional(),
  cloudfrontUrl: z.string().url('Invalid CloudFront URL').optional(),
  alt: z.string().max(200, 'Alt text must be less than 200 characters').optional(),
  caption: z.string().max(500, 'Caption must be less than 500 characters').optional(),
  category: z.string().max(100, 'Category must be less than 100 characters').optional(),
  order: z.number().int().min(0).default(0),
  // Image crop data
  crop: imageCropSchema,
});

export const imageGalleryCustomColorsSchema = z
  .object({
    background: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    text: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    title: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    subtitle: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    caption: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    filterButton: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    filterButtonActive: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    buttonBg: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    buttonText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
  })
  .optional();

export const imageGalleryDataSchema = z.object({
  title: z.string().max(200, 'Title must be less than 200 characters').optional(),
  subtitle: z.string().max(500, 'Subtitle must be less than 500 characters').optional(),
  showTitle: z.boolean().default(true),
  showSubtitle: z.boolean().default(true),
  items: z.array(imageGalleryItemSchema).min(1, 'At least one gallery item is required'),
  layout: z.enum(['grid', 'masonry', 'carousel', 'divider', 'slideshow']).default('grid'),
  columns: z.number().int().min(1).max(6).default(3),
  spacing: z.enum(['compact', 'normal', 'spacious']).default('normal'),
  aspectRatio: z.enum(['auto', 'square', '16:9', '4:3']).default('auto'),
  captionPosition: z.enum(['below', 'overlay', 'none']).default('below'),
  lightbox: z.boolean().default(true),
  lazyLoading: z.boolean().default(true),
  filtering: z.boolean().default(false),
  categories: z.array(z.string()).default([]),
  customColors: imageGalleryCustomColorsSchema,
  // Optional call-to-action buttons
  buttons: z.array(buttonSchema).optional(),
});

export const imageGalleryComponentSchema = baseComponentSchema.extend({
  type: z.literal('image-gallery'),
  data: imageGalleryDataSchema,
});

// Type exports
export type ImageGalleryItem = z.infer<typeof imageGalleryItemSchema>;
export type ImageGalleryData = z.infer<typeof imageGalleryDataSchema>;
export type ImageGalleryComponent = z.infer<typeof imageGalleryComponentSchema>;
